import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Sparkles, Edit3, Gift, ShieldCheck } from "lucide-react";
import BackButton from "@/components/BackButton";

const PORTAL_META = {
  ORGANIZATION: { label: "Organization", icon: "🏭", color: "bg-orange-100 text-orange-900 border-orange-200" },
  DISTRIBUTOR:  { label: "Distributor",  icon: "🚚", color: "bg-blue-100 text-blue-900 border-blue-200" },
  VENDOR:       { label: "Vendor",       icon: "🏬", color: "bg-emerald-100 text-emerald-900 border-emerald-200" },
};

export default function PlatformTrials() {
  const [trials, setTrials] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const { data } = await api.get("/platform/plans");
    setTrials(data.filter((p) => p.is_trial));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEdit(p);
    setForm({
      name: p.name || "", tagline: p.tagline || "",
      trial_portal: p.trial_portal || "ORGANIZATION",
      trial_days: p.trial_days ?? 14,
      trial_max_users: p.trial_max_users ?? 5,
      trial_max_transactions: p.trial_max_transactions ?? 500,
      trial_grace_days: p.trial_grace_days ?? 3,
      storage_gb: p.storage_gb ?? 5,
      max_users_default: p.max_users_default ?? 5,
      marketplace_access: !!p.marketplace_access,
      support_level: p.support_level || "Community",
      features: (p.features || []).join("\n"),
      active: p.active !== false,
      is_trial: true,
      base_price_monthly: 0, base_price_yearly: 0,
      currency: "INR", ai_credits: 0, highlight: false, sort_order: 0,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        features: (form.features || "").split("\n").map((s) => s.trim()).filter(Boolean),
        trial_days: Number(form.trial_days || 0),
        trial_max_users: Number(form.trial_max_users || 0),
        trial_max_transactions: Number(form.trial_max_transactions || 0),
        trial_grace_days: Number(form.trial_grace_days || 0),
        storage_gb: Number(form.storage_gb || 0),
        max_users_default: Number(form.max_users_default || 0),
      };
      await api.patch(`/platform/plans/${edit.id}`, payload);
      toast.success("Trial plan updated");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (p) => {
    try { await api.patch(`/platform/plans/${p.id}`, { active: !p.active }); toast.success(!p.active ? "Trial enabled" : "Trial disabled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-platform-trials">
      <BackButton />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700">
            <ShieldCheck className="w-3 h-3" /> Super Admin only
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2 mt-3">
            <Gift className="w-7 h-7 text-primary" /> Free trial plans
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            One trial plan per portal (Organization / Distributor / Vendor). Configure trial duration, user cap, transaction cap,
            grace period and included modules. Toggle active to open or close a trial channel.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {trials.map((t) => {
          const meta = PORTAL_META[t.trial_portal] || PORTAL_META.ORGANIZATION;
          return (
            <div key={t.id} className="surface-card p-5 space-y-3 relative overflow-hidden" data-testid={`trial-card-${t.code}`}>
              <div className="absolute -top-4 -right-4 text-5xl opacity-20">{meta.icon}</div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={`font-mono text-[10px] ${meta.color}`}>{meta.label}</Badge>
                <Badge variant="outline" className={`font-mono text-[10px] ${t.active ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-900"}`}>{t.active ? "ACTIVE" : "OFF"}</Badge>
              </div>
              <div>
                <div className="font-display text-lg font-black">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.tagline}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Duration</div><div className="font-display font-black text-lg mt-0.5">{t.trial_days}<span className="text-sm font-normal text-muted-foreground"> days</span></div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Grace period</div><div className="font-display font-black text-lg mt-0.5">+{t.trial_grace_days ?? 0}<span className="text-sm font-normal text-muted-foreground"> days</span></div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Max users</div><div className="font-mono font-bold text-sm mt-0.5">{t.trial_max_users}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Max transactions</div><div className="font-mono font-bold text-sm mt-0.5">{t.trial_max_transactions}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Storage</div><div className="font-mono font-bold text-sm mt-0.5">{t.storage_gb} GB</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground tracking-wider">Marketplace</div><div className="font-mono font-bold text-sm mt-0.5">{t.marketplace_access ? "Yes" : "No"}</div></div>
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-border">
                <Button size="sm" variant="outline" onClick={() => startEdit(t)} data-testid={`trial-edit-${t.code}`}><Edit3 className="w-3.5 h-3.5 mr-1" /> Configure</Button>
                <div className="flex-1" />
                <Switch checked={!!t.active} onCheckedChange={() => toggleActive(t)} data-testid={`trial-toggle-${t.code}`} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Portal</TableHead><TableHead>Days</TableHead>
            <TableHead>Grace</TableHead><TableHead>Max users</TableHead><TableHead>Max transactions</TableHead>
            <TableHead>Storage</TableHead><TableHead>Active</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {trials.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-mono text-xs">{t.code}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{t.trial_portal}</Badge></TableCell>
                <TableCell className="font-mono">{t.trial_days}</TableCell>
                <TableCell className="font-mono">{t.trial_grace_days ?? 0}</TableCell>
                <TableCell className="font-mono">{t.trial_max_users}</TableCell>
                <TableCell className="font-mono">{t.trial_max_transactions}</TableCell>
                <TableCell className="font-mono">{t.storage_gb} GB</TableCell>
                <TableCell>
                  <Switch checked={!!t.active} onCheckedChange={() => toggleActive(t)} />
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => startEdit(t)}><Edit3 className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Configure {edit?.trial_portal} trial</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="trial-name" /></div>
            <div><Label>Tagline</Label><Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
            <div><Label>Portal</Label>
              <Select value={form.trial_portal} onValueChange={(v) => setForm({ ...form, trial_portal: v })}>
                <SelectTrigger data-testid="trial-portal"><SelectValue /></SelectTrigger>
                <SelectContent>{["ORGANIZATION", "DISTRIBUTOR", "VENDOR"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Trial duration (days)</Label><Input type="number" value={form.trial_days || 0} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} data-testid="trial-days" /></div>
            <div><Label>Grace period after expiry (days)</Label><Input type="number" value={form.trial_grace_days || 0} onChange={(e) => setForm({ ...form, trial_grace_days: e.target.value })} data-testid="trial-grace" /></div>
            <div><Label>Max users</Label><Input type="number" value={form.trial_max_users || 0} onChange={(e) => setForm({ ...form, trial_max_users: e.target.value })} data-testid="trial-max-users" /></div>
            <div><Label>Max transactions</Label><Input type="number" value={form.trial_max_transactions || 0} onChange={(e) => setForm({ ...form, trial_max_transactions: e.target.value })} data-testid="trial-max-tx" /></div>
            <div><Label>Storage (GB)</Label><Input type="number" value={form.storage_gb || 0} onChange={(e) => setForm({ ...form, storage_gb: e.target.value })} /></div>
            <div><Label>Default users</Label><Input type="number" value={form.max_users_default || 0} onChange={(e) => setForm({ ...form, max_users_default: e.target.value })} /></div>
            <div><Label>Support level</Label><Input value={form.support_level || ""} onChange={(e) => setForm({ ...form, support_level: e.target.value })} /></div>
            <div className="flex items-center justify-between pt-6"><Label>Marketplace access</Label><Switch checked={!!form.marketplace_access} onCheckedChange={(v) => setForm({ ...form, marketplace_access: v })} /></div>
            <div className="flex items-center justify-between pt-6"><Label>Trial active</Label><Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="trial-active" /></div>
            <div className="col-span-2"><Label>Features (one per line)</Label><textarea rows={4} className="w-full border border-input rounded-md p-2 text-sm font-mono" value={form.features || ""} onChange={(e) => setForm({ ...form, features: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="trial-save">Save trial config</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
